import React, { useId } from 'react';
import { Check } from 'lucide-react';
import type { ProjectIntent } from '../../../types/siteRag';

type ScreeningMode = NonNullable<ProjectIntent['screeningMode']>;
interface ScreeningApproachPickerProps {
  value: ScreeningMode;
  onChange: (value: ScreeningMode) => void;
  disabled?: boolean;
  testIdPrefix: string;
}

const APPROACHES = [
  {
    value: 'recommend',
    label: 'Recommend an approach',
    description: 'Compare size, power and cooling options using site evidence. No proposal needed.',
  },
  {
    value: 'evaluate-proposal',
    label: 'Evaluate my proposal',
    description: 'Assess the workload, power and timing you provide. Ranges and unknowns are welcome.',
  },
] as const;

/** Native radios keep selection, focus and arrow-key navigation consistent. */
export function ScreeningApproachPicker({ value, onChange, disabled = false, testIdPrefix }: ScreeningApproachPickerProps) {
  const groupId = useId();
  const recommending = value === 'recommend';
  return (
    <fieldset disabled={disabled} className="min-w-0 max-w-4xl space-y-3"
      aria-describedby={`${groupId}-requirements ${groupId}-help`} data-testid={`${testIdPrefix}-screening-approach`}>
      <legend className="text-sm font-semibold text-text-primary">Screening approach</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        {APPROACHES.map(approach => {
          const selected = approach.value === value;
          const id = `${groupId}-${approach.value}`;
          return (
            <label key={approach.value} data-selected={selected ? 'true' : 'false'}
              className={`flex min-w-0 items-start gap-3 rounded-xl border p-4 text-left transition-colors motion-reduce:transition-none focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-primary ${
                selected ? 'selected-accent' : 'border-border bg-surface-2 text-text-primary hover:border-primary'
              } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
              <input type="radio" name={groupId} value={approach.value} checked={selected}
                className="sr-only" aria-labelledby={`${id}-label`} aria-describedby={`${id}-description`}
                data-testid={`${testIdPrefix}-screening-mode-${approach.value}`}
                onChange={() => { if (!disabled && !selected) onChange(approach.value); }} />
              <span aria-hidden="true" className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-current">
                {selected && <Check className="h-3.5 w-3.5" />}
              </span>
              <span className="min-w-0 flex-1 space-y-1.5">
                <span className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                  <span id={`${id}-label`} className="text-sm font-semibold">{approach.label}</span>
                  {selected && <span aria-hidden="true" className="text-xs font-medium">Selected</span>}
                </span>
                <span id={`${id}-description`} className={`block max-w-[52ch] text-xs leading-relaxed ${selected ? 'opacity-90' : 'text-text-secondary'}`}>
                  {approach.description}
                </span>
              </span>
            </label>
          );
        })}
      </div>
      <div className="space-y-1 border-l-2 border-primary pl-3" data-testid={`${testIdPrefix}-screening-input-guidance`}>
        <p className="text-sm font-medium text-text-primary">
          {recommending ? 'Start with site evidence' : 'Describe the proposal you want assessed'}
        </p>
        <p id={`${groupId}-requirements`} className="max-w-[75ch] text-xs leading-relaxed text-text-secondary">
          {recommending
            ? 'Check the site location and evidence, and add any known constraints in Site context. Workload, power size and timing can stay undecided. Screening will recommend conditional options.'
            : 'Add the workload, power size and target timing you know below. Ranges and unknown answers are accepted. Detailed technology and cooling choices can wait until you review Screening.'}
        </p>
      </div>
      <p id={`${groupId}-help`} className="max-w-[75ch] text-xs leading-relaxed text-text-secondary">
        Your saved choices stay in place. Choosing an approach does not run a report or apply a recommendation.
      </p>
    </fieldset>
  );
}
